import { getHistoricalData } from "@/_core/data/api_calls/candle_data/getHistoricalData";
import { formatDate_YMDHMS, roundDateToFloor } from "@/_core/_helpers/_helpers";
import gql from "graphql-tag";

/**
 * Retrieves historical commodity data using Redis cache or falls back to API queries.
 *
 * @param {object} _this - The context object containing relevant data.
 * @param {object} [assetExtraParams={}] - Additional parameters related to the commodity.
 * @param {number} assetExtraParams.commodityNumber - The primary identifier for the commodity.
 * @param {number} assetExtraParams.contract - The contract identifier for the commodity.
 * @returns {Promise<object>} A promise resolving to the historical commodity data.
 */
export function getCandleCommodities(_this, assetExtraParams = {}) {
  let fast = true; // Flag to determine whether to use Redis cache.
  if (fast) {
    // GraphQL query to retrieve cached data from Redis.
    const paramsRedis = gql`
      query($key: String!) {
        getRedisData(key: $key)
      }
    `;
    // Construct query parameters for Redis retrieval.
    const variablesRedis = {
      format: "json",
      observation_date: formatDate_YMDHMS(
        roundDateToFloor(_this.initCommodityDate)
      ),
      key: "COM_HIST_" + assetExtraParams.commodityNumber,
      contract: assetExtraParams.contract
    };
    let boolContract = true; // Boolean flag to verify if selected contract matches.
    // Validate if a selected contract exists in the context object.
    if (_this.commodityDefs !== undefined) {
      let keys = Object.keys(_this.commodityDefs);
      if (keys.includes("selected_contract")) {
        boolContract =
          _this.commodityDefs.selected_contract === assetExtraParams.contract;
      }
    }
    // Attempt to fetch historical data from Redis.
    return getHistoricalData(_this, {
      params: paramsRedis,
      variables: variablesRedis,
      source: "getRedisData",
      boolContract
    }).catch(error => {
      console.error("Error from getHistoricalData Redis:", error); // Log error if Redis retrieval fails.
      // GraphQL query for fetching commodity technical analysis data.
      const paramsTechnicalAnalysis = gql`
        query(
          $observation_date: String!
          $commodity: Int!
          $contract: Int!
          $format: String!
        ) {
          commodity_technical_analysis_historical(
            observation_date: $observation_date
            commodity: $commodity
            contract: $contract
            format: $format
          ) {
            avg_gain
            avg_loss
            bband_l1
            bband_l2
            bband_u1
            bband_u2
            bband_ave
            close
            cluster
            continuous_contract
            contract_month_year
            ewma
            find_max_long
            find_max_medium
            find_max_short
            find_min_long
            find_min_medium
            find_min_short
            fk_commodities
            ha_close
            ha_high
            ha_low
            ha_open
            high
            interp_trend_long
            interp_trend_medium
            interp_trend_short
            last_trading_date
            low
            ma20
            ma40
            ma_40
            mvol
            observation_date
            open
            openinterest
            pattern_dist
            pattern_selected
            pk_idcommodityhistorical
            rsi
            settle
            volumes
            y_pattern
          }
        }
      `;
      // Construct query parameters for commodity technical analysis.
      const variablesTechnicalAnalysis = {
        format: "json",
        observation_date: formatDate_YMDHMS(
          roundDateToFloor(_this.initCommodityDate)
        ),
        commodity: assetExtraParams.commodityNumber,
        contract: assetExtraParams.contract
      };
      let boolContract = true;
      // Validate if a selected contract exists in the context object.
      if (_this.commodityDefs !== undefined) {
        let keys = Object.keys(_this.commodityDefs);
        if (keys.includes("selected_contract")) {
          boolContract =
            _this.commodityDefs.selected_contract === assetExtraParams.contract;
        }
      }
      // Attempt to fetch historical data via technical analysis query.
      return getHistoricalData(_this, {
        params: paramsTechnicalAnalysis,
        variables: variablesTechnicalAnalysis,
        source: "commodity_technical_analysis_historical",
        boolContract
      }).catch(error => {
        console.error("Error from technical_analysis_historical:", error);
        throw error;
      });
    });
  } else {
    console.error("Not using Redis, Warning!"); // Log a warning when Redis is not used.
    // GraphQL query for fetching commodity technical analysis data.
    const paramsTechnicalAnalysis = gql`
      query(
        $observation_date: String!
        $commodity: Int!
        $contract: Int!
        $format: String!
      ) {
        commodity_technical_analysis_historical(
          observation_date: $observation_date
          commodity: $commodity
          contract: $contract
          format: $format
        ) {
          avg_gain
          avg_loss
          bband_l1
          bband_l2
          bband_u1
          bband_u2
          bband_ave
          close
          cluster
          continuous_contract
          contract_month_year
          ewma
          find_max_long
          find_max_medium
          find_max_short
          find_min_long
          find_min_medium
          find_min_short
          fk_commodities
          ha_close
          ha_high
          ha_low
          ha_open
          high
          interp_trend_long
          interp_trend_medium
          interp_trend_short
          last_trading_date
          low
          ma20
          ma40
          ma_40
          mvol
          observation_date
          open
          openinterest
          pattern_dist
          pattern_selected
          pk_idcommodityhistorical
          rsi
          settle
          volumes
          y_pattern
        }
      }
    `;
    // Construct query parameters for commodity technical analysis.
    const variablesTechnicalAnalysis = {
      format: "json",
      observation_date: formatDate_YMDHMS(
        roundDateToFloor(_this.initCommodityDate)
      ),
      commodity: assetExtraParams.commodityNumber,
      contract: assetExtraParams.contract
    };
    let boolContract = true;
    // Validate if a selected contract exists in the context object.
    if (_this.commodityDefs !== undefined) {
      let keys = Object.keys(_this.commodityDefs);
      if (keys.includes("selected_contract")) {
        boolContract =
          _this.commodityDefs.selected_contract === assetExtraParams.contract;
      }
    }

    // Attempt to fetch historical data via technical analysis query.
    return getHistoricalData(_this, {
      params: paramsTechnicalAnalysis,
      variables: variablesTechnicalAnalysis,
      source: "commodity_technical_analysis_historical",
      boolContract
    }).catch(error => {
      console.error("Error from technical_analysis_historical:", error);
      throw error;
    });
  }
}
